import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Check, Shield, Users, Headphones, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { toast } from "sonner";

const enterpriseFeatures = [
  "Unlimited projects & exports",
  "Custom pricing & contracts",
  "SSO / SAML authentication",
  "Advanced permissions & audit logs",
  "Dedicated account manager",
  "Priority SLA support",
  "Custom integrations & API",
  "Dedicated onboarding & training",
];

export default function Enterprise() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: user?.email || "", company: "", companySize: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) { toast.error("Please fill in required fields."); return; }
    setSubmitting(true);
    await supabase.from("enterprise_leads").insert({
      user_id: user?.id || null,
      name: form.name,
      email: form.email,
      company: form.company,
      company_size: form.companySize,
      message: form.message,
    });
    setSubmitting(false);
    setSubmitted(true);
    toast.success("Demo request submitted! We'll be in touch within 24 hours.");
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="container flex items-center justify-between py-5">
        <span className="font-heading text-xl font-medium tracking-tight text-foreground cursor-pointer" onClick={() => navigate("/")}>
          oplif<span className="text-primary">.</span>
        </span>
        <Button size="sm" variant="outline" onClick={() => navigate("/pricing")}>View Pricing</Button>
      </nav>

      <section className="container pt-8 pb-20 max-w-5xl">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Info */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-foreground mb-4">
              Enterprise-grade business planning
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Custom solutions for organizations that need advanced security, compliance, and dedicated support.
            </p>
            <ul className="space-y-3">
              {enterpriseFeatures.map((f) => (
                <li key={f} className="flex items-center gap-3 text-foreground">
                  <Check className="w-5 h-5 text-primary shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-6 mt-8 pt-6 border-t border-border">
              {[
                { icon: Shield, label: "SOC 2 Compliant" },
                { icon: Users, label: "Unlimited Seats" },
                { icon: Headphones, label: "24/7 Support" },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <t.icon className="w-4 h-4" /> {t.label}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
            {submitted ? (
              <div className="bg-card rounded-2xl p-8 border border-border shadow-card text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading text-2xl font-medium text-foreground mb-2">Request Received!</h2>
                <p className="text-muted-foreground mb-6">Our team will reach out within 24 hours to schedule your personalized demo.</p>
                <Button onClick={() => navigate("/")} className="gap-2">Back to Home <ArrowRight className="w-4 h-4" /></Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-8 border border-border shadow-card space-y-4">
                <h2 className="font-heading text-xl font-medium text-foreground mb-2">Book a Demo</h2>
                <p className="text-sm text-muted-foreground mb-4">Tell us about your organization and we'll set up a personalized walkthrough.</p>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Full Name *</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Work Email *</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Company *</label>
                  <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Acme Inc." />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Company Size</label>
                  <Input value={form.companySize} onChange={(e) => setForm({ ...form, companySize: e.target.value })} placeholder="50-200 employees" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Tell us about your needs</label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="We're looking to..." rows={3} />
                </div>
                <Button type="submit" className="w-full gap-2" disabled={submitting}>
                  {submitting ? "Submitting..." : "Request a Demo"} <ArrowRight className="w-4 h-4" />
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
